import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'

export function useIsSuperAdmin() {
  const { user, getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['is-super-admin', user?.sub],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get('/superadmin/check')
      return response.data.is_super_admin
    },
    enabled: !!user,
  })
}

export function useAllTeams() {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['superadmin-teams'],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get('/superadmin/teams')
      return response.data
    },
  })
}

export function useAllUsers() {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['superadmin-users'],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get('/superadmin/users')
      return response.data
    },
  })
}

export function useDeleteTeam() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (teamId: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.delete(`/superadmin/teams/${teamId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-teams'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })
}

export function useDeleteGame() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (gameId: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.delete(`/superadmin/games/${gameId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['team-stats'] })
    },
  })
}

export function useDeleteUser() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.delete(`/superadmin/users/${userId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-users'] })
      queryClient.invalidateQueries({ queryKey: ['team-users'] })
    },
  })
}

